import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ASSET_URL } from '../lib/service-registry';
import {
  ChartBarIcon,
  BuildingOffice2Icon,
  ClipboardDocumentCheckIcon,
  UserIcon,
  XMarkIcon,
  SparklesIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  DocumentCheckIcon,
  StarIcon,
  ArrowUpCircleIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  CogIcon,
  LightBulbIcon,
  UserGroupIcon,
  ServerStackIcon,
  ClockIcon,
  FingerPrintIcon,
  CalendarDaysIcon,
  MegaphoneIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useEffectiveRole } from '../hooks/useEffectiveRole';
import { useDemo, DEMO_COMPANIES } from '../demo/DemoContext';
import { useApiService } from '../hooks/useApiService';
import { View, VIEW_TO_PATH } from '../routes';
import { fetchMenuPermissions, getMenuPermission, MenuPermissionMap, MenuPermission } from '../services/api/menuPermissions';

interface MenuItem {
  id: View;
  label: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;
  allowedRoles: string[];
  section: string;
  /** Resolved permission from admin API: 'RW' = full, 'R' = read-only, null = hidden */
  permission?: MenuPermission;
}

interface SidebarProps {
  currentView: View;
  setCurrentView: (view: View) => void;
  isOpen?: boolean;
  onClose?: () => void;
  user?: { username: string; role: string; display_name?: string } | null;
  onLogout?: () => void;
}

const ROLES = [
  { value: 'admin', label: '관리자', color: '#d93025' },
  { value: 'hr_manager', label: '매니저', color: '#1a73e8' },
  { value: 'org_head', label: '담당자', color: '#1e8e3e' },
  { value: 'employee', label: '조회자', color: '#80868b' },
] as const;

const Sidebar: React.FC<SidebarProps> = ({ currentView, setCurrentView, isOpen, onClose, user, onLogout }) => {
  const userRole = useEffectiveRole();
  const { isDemo, toggleDemo, setRoleOverride, setViewAsEmployee, companyOverride, setCompanyOverride } = useDemo();
  const apiService = useApiService();
  const isAdmin = user?.role === 'admin';
  const currentRoleInfo = ROLES.find(r => r.value === userRole) || ROLES[3];

  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [menuPermissions, setMenuPermissions] = useState<MenuPermissionMap>({});

  // MYB-364: Fetch dynamic menu permissions from admin.tscorp.ai
  useEffect(() => {
    fetchMenuPermissions().then(setMenuPermissions).catch(() => {
      // Fallback already handled inside fetchMenuPermissions
    });
  }, []);

  const loadFavorites = useCallback(async () => {
    try {
      const resp = await apiService.getFavorites();
      const favs = resp?.favorites || [];
      setFavoriteIds(new Set(favs.filter((f: any) => f.target_type === 'menu').map((f: any) => f.target_id)));
    } catch { /* ignore */ }
  }, [apiService]);

  useEffect(() => { loadFavorites(); }, [loadFavorites]);

  const toggleFavorite = async (menuId: string, label: string) => {
    const isFav = favoriteIds.has(menuId);
    try {
      if (isFav) {
        await apiService.removeFavorite('menu', menuId);
        setFavoriteIds(prev => { const n = new Set(prev); n.delete(menuId); return n; });
      } else {
        await apiService.addFavorite('menu', menuId, label);
        setFavoriteIds(prev => new Set(prev).add(menuId));
      }
    } catch { /* ignore */ }
  };

  const menuItems: MenuItem[] = [
    // 메인
    { id: 'dashboard', label: '대시보드', icon: ChartBarIcon, allowedRoles: ['admin', 'hr_manager'], section: '메인' },
    { id: 'mypage', label: '마이페이지', icon: UserIcon, allowedRoles: ['admin', 'hr_manager', 'org_head', 'employee'], section: '메인' },
    { id: 'workflow', label: '결재함', icon: DocumentCheckIcon, allowedRoles: ['admin', 'hr_manager', 'org_head', 'employee'], section: '메인' },
    // 인사운영
    { id: 'org_mgmt', label: '조직 관리', icon: BuildingOffice2Icon, allowedRoles: ['admin', 'hr_manager', 'org_head'], section: '인사운영' },
    { id: 'recruitment', label: '채용 관리', icon: UsersIcon, allowedRoles: ['admin', 'hr_manager'], section: '인사운영' },
    { id: 'eval_mgmt', label: '평가 관리', icon: ClipboardDocumentCheckIcon, allowedRoles: ['admin', 'hr_manager', 'org_head', 'employee'], section: '인사운영' },
    { id: 'talent_dev', label: '역량 육성', icon: LightBulbIcon, allowedRoles: ['admin', 'hr_manager', 'org_head'], section: '인사운영' },
    { id: 'promotion', label: '승진 관리', icon: ArrowUpCircleIcon, allowedRoles: ['admin', 'hr_manager'], section: '인사운영' },
    // 근태 — MYB-361: 매일 사용하는 근무/출퇴근/연차 기능 집합
    { id: 'attendance', label: '근무 관리', icon: FingerPrintIcon, allowedRoles: ['admin', 'hr_manager', 'org_head', 'employee'], section: '근태' },
    { id: 'overtime', label: '초과근무 관리', icon: ClockIcon, allowedRoles: ['admin', 'hr_manager', 'org_head', 'employee'], section: '근태' },
    { id: 'leave', label: '연차/휴가', icon: CalendarDaysIcon, allowedRoles: ['admin', 'hr_manager', 'org_head', 'employee'], section: '근태' },
    // 소통 — MYB-380/381: 공지/게시판 + 주간보고
    { id: 'notice_board', label: '공지 / 게시판', icon: MegaphoneIcon, allowedRoles: ['admin', 'hr_manager', 'org_head', 'employee'], section: '소통' },
    { id: 'weekly_report', label: '주간 보고', icon: DocumentTextIcon, allowedRoles: ['admin', 'hr_manager', 'org_head', 'employee'], section: '소통' },
    // 보상 & 준수
    { id: 'compensation', label: '보상 관리', icon: BanknotesIcon, allowedRoles: ['admin', 'hr_manager'], section: '보상 & 준수' },
    { id: 'compliance', label: '컴플라이언스', icon: ShieldCheckIcon, allowedRoles: ['admin', 'hr_manager', 'org_head', 'employee'], section: '보상 & 준수' },
    // 전략 & 분석
    { id: 'talent_pool', label: '인재 관리', icon: UserGroupIcon, allowedRoles: ['admin', 'hr_manager'], section: '전략 & 분석' },
    { id: 'hr_analytics', label: '인사 분석', icon: SparklesIcon, allowedRoles: ['admin', 'hr_manager', 'org_head'], section: '전략 & 분석' },
    // 시스템
    { id: 'system_settings', label: '시스템 설정', icon: CogIcon, allowedRoles: ['admin'], section: '시스템' },
  ];

  // MYB-364: Filter and annotate menu items with dynamic permissions from admin API
  const filteredMenuItems = menuItems
    .map(item => {
      const perm = getMenuPermission(menuPermissions, item.id, userRole);
      return { ...item, permission: perm };
    })
    .filter(item => item.permission !== null);

  let lastSection = '';

  return (
    <>
      {isOpen && <div className="sidebar-backdrop" onClick={onClose} />}
      <aside className={`app-sidebar ${isOpen ? 'sidebar-open' : ''}`} aria-label="사이드바 메뉴">
        <div className="sidebar-drawer-header">
          <span>메뉴</span>
          <button className="sidebar-drawer-close" onClick={onClose} aria-label="메뉴 닫기">
            <XMarkIcon style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
        </div>
        {/* TSCORP 서비스 스위처 */}
        <div className="sidebar-service-switcher">
          <div className="sidebar-service-label">TSCORP Platform</div>
          <div className="sidebar-service-list">
            <a href={ASSET_URL} className="sidebar-service-item">
              <ServerStackIcon aria-hidden="true" />
              <span>자산 관리</span>
            </a>
            <span className="sidebar-service-item active" aria-current="true">
              <UsersIcon aria-hidden="true" />
              <span>인사 관리</span>
            </span>
          </div>
        </div>

        <nav role="navigation" aria-label="주 메뉴" style={{ flex: 1, overflowY: 'auto' }}>
          <ul className="sidebar-nav">
          {/* 즐겨찾기 섹션 - 카드형 */}
          {favoriteIds.size > 0 && (() => {
            const favItems = filteredMenuItems.filter(item => favoriteIds.has(item.id));
            if (favItems.length === 0) return null;
            return (
              <li className="sidebar-favorites-card" style={{ listStyle: 'none' }}>
                <div className="sidebar-favorites-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <StarSolidIcon style={{ width: 13, height: 13, color: '#f59e0b' }} />
                    <span>즐겨찾기</span>
                  </div>
                  <span className="sidebar-favorites-count">{favItems.length}</span>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {favItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentView === item.id;
                    return (
                      <li key={`fav-${item.id}`} className="sidebar-favorites-item">
                        <Link
                          to={VIEW_TO_PATH[item.id]}
                          onClick={() => setCurrentView(item.id)}
                          className={`sidebar-button ${isActive ? 'active' : ''}`}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <Icon aria-hidden="true" />
                          {item.label}
                          {item.id === 'weekly_report' && (
                            <span
                              title="실험 중 기능"
                              style={{
                                marginLeft: 6,
                                padding: '1px 6px',
                                borderRadius: 999,
                                fontSize: '0.625rem',
                                fontWeight: 600,
                                background: '#fef7e0',
                                color: '#e37400',
                                border: '1px solid #fde293',
                                lineHeight: 1.4,
                              }}
                            >
                              실험중
                            </span>
                          )}
                        </Link>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id, item.label); }}
                          className="sidebar-favorites-unpin"
                          aria-label={`${item.label} 즐겨찾기 해제`}
                        >
                          <XMarkIcon style={{ width: 12, height: 12 }} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })()}
          {/* MYB-295: Exclude favorited items from main list — they appear only in the favorites section above */}
          {(() => {
            const mainNavItems = filteredMenuItems.filter((item) => !favoriteIds.has(item.id));
            let lastMainSection = '';
            return mainNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            const showSection = item.section !== lastMainSection && item.section !== '메인';
            lastMainSection = item.section;
            return (
              <React.Fragment key={item.id}>
                {showSection && (
                  <li className="sidebar-section-label" aria-hidden="true">
                    {item.section}
                  </li>
                )}
                <li>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Link
                      to={VIEW_TO_PATH[item.id]}
                      onClick={() => setCurrentView(item.id)}
                      className={`sidebar-button ${isActive ? 'active' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                      aria-label={`${item.label} 페이지로 이동${item.permission === 'R' ? ' (읽기 전용)' : ''}`}
                      style={{ flex: 1 }}
                    >
                      <Icon aria-hidden="true" />
                      {item.label}
                      {item.id === 'weekly_report' && (
                        <span
                          title="실험 중 기능"
                          style={{
                            marginLeft: 6,
                            padding: '1px 6px',
                            borderRadius: 999,
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            background: '#fef7e0',
                            color: '#e37400',
                            border: '1px solid #fde293',
                            lineHeight: 1.4,
                          }}
                        >
                          실험중
                        </span>
                      )}
                      {item.permission === 'R' && (
                        <span className="sidebar-readonly-badge" title="읽기 전용">R</span>
                      )}
                    </Link>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id, item.label); }}
                      className="sidebar-star-btn"
                      aria-label={`${item.label} 즐겨찾기 추가`}
                    >
                      <StarIcon style={{ width: 14, height: 14 }} />
                    </button>
                  </div>
                </li>
              </React.Fragment>
            );
          });
          })()}
          </ul>
        </nav>

        {/* MYB-413: User info + logout (single location for account actions) */}
        {user && (
          <div className="sidebar-user-info">
            <div className="sidebar-user-avatar" style={{ background: '#1e8e3e' }}>
              {(user.display_name || user.username || '?')[0].toUpperCase()}
            </div>
            <div className="sidebar-user-detail">
              <div className="sidebar-user-name">{user.display_name || user.username}</div>
              <div className="sidebar-user-email">{user.username}</div>
              <div className="sidebar-user-role">
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: currentRoleInfo.color, flexShrink: 0 }} />
                {currentRoleInfo.label}
              </div>
            </div>
            {onLogout && (
              <button
                onClick={onLogout}
                className="sidebar-user-logout"
                aria-label="로그아웃"
                title="로그아웃"
                style={{
                  marginLeft: 'auto', padding: '0.375rem', borderRadius: '0.5rem',
                  border: 'none', background: 'transparent', color: '#5f6368',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  flexShrink: 0, transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f3f4'; e.currentTarget.style.color = '#d93025'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#5f6368'; }}
              >
                <ArrowRightOnRectangleIcon style={{ width: '1.125rem', height: '1.125rem' }} />
              </button>
            )}
          </div>
        )}

        {/* 모바일 전용: 사이드바 하단 컨트롤 (역할/데모/로그아웃) */}
        <div className="sidebar-drawer-footer">
          {/* 관리자 컨트롤 */}
          {isAdmin && (
            <div className="sidebar-footer-controls">
              {/* 역할 전환 */}
              <div className="sidebar-footer-row">
                <span className="sidebar-footer-label">역할 전환</span>
                <select aria-label="역할 전환"
                  value={userRole}
                  onChange={(e) => { setViewAsEmployee(null); setRoleOverride(e.target.value === user?.role ? null : e.target.value); }}
                  className="sidebar-footer-select"
                >
                  {ROLES.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              {/* 법인 전환 (데모 모드 only) */}
              {isDemo && (
                <div className="sidebar-footer-row">
                  <span className="sidebar-footer-label">법인 전환</span>
                  <select aria-label="법인 전환"
                    value={companyOverride || 1}
                    onChange={(e) => setCompanyOverride(parseInt(e.target.value) === 1 ? null : parseInt(e.target.value))}
                    className="sidebar-footer-select"
                  >
                    {DEMO_COMPANIES.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {/* 데모 모드 */}
              <div className="sidebar-footer-row">
                <span className="sidebar-footer-label">데모 모드</span>
                <button
                  onClick={toggleDemo}
                  className={`sidebar-footer-toggle ${isDemo ? 'active' : ''}`}
                  aria-label={isDemo ? '데모 모드 끄기' : '데모 모드 켜기'}
                >
                  <span className="sidebar-footer-toggle-track">
                    <span className="sidebar-footer-toggle-thumb" />
                  </span>
                  <span>{isDemo ? 'ON' : 'OFF'}</span>
                </button>
              </div>
            </div>
          )}

          {/* 로그아웃 */}
          {onLogout && (
            <button onClick={onLogout} className="sidebar-footer-logout">
              <ArrowRightOnRectangleIcon style={{ width: '1.125rem', height: '1.125rem' }} />
              <span>로그아웃</span>
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
